import { displayDateUS } from "../../utils/dateUtils";
import { CHECKLIST_ITEMS } from "../../pages/Application/OrientationChecklist";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseOrientationChecklistFromApiResponse(
  response,
  targetUserId,
) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.checklist) {
    return result.checklist;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.contractorName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

/**
 * @param {unknown[]} apiItems
 */
function mergeChecklistRows(apiItems) {
  const raw = Array.isArray(apiItems) ? apiItems : [];
  const formatted = raw.map((item) => {
    if (!item || typeof item !== "object") return item;
    const rec = /** @type {{ dateOfOrientation?: string }} */ (item);
    const d = rec.dateOfOrientation;
    return {
      ...item,
      dateOfOrientation:
        d && typeof d === "string" && d.includes("T") ? d.split("T")[0] : d,
    };
  });

  return CHECKLIST_ITEMS.map((defaultItem, index) => {
    const itemNumber = index + 1;
    const existing = formatted.find(
      (i) =>
        i &&
        typeof i === "object" &&
        /** @type {{ itemNumber?: number }} */ (i).itemNumber === itemNumber,
    );
    return {
      itemNumber,
      key: defaultItem.key,
      text: defaultItem.text,
      subItems: defaultItem.subItems,
      contractorInitials: existing
        ? String(
            /** @type {{ contractorInitials?: string }} */ (existing)
              .contractorInitials || "",
          )
        : "",
      dateOfOrientation: existing
        ? /** @type {{ dateOfOrientation?: string }} */ (existing)
            .dateOfOrientation || ""
        : "",
    };
  });
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/** @param {{ key: string; text: string; subItems?: string[] }} itemDef */
function buildChecklistDescriptionCell(itemDef) {
  const key = escapeHtml(String(itemDef.key));
  if (itemDef.text === "Orientation content for all contractors") {
    const subs = (itemDef.subItems || [])
      .map((sub) => {
        const space = sub.indexOf(" ");
        const head = space >= 0 ? sub.slice(0, space) : sub;
        const rest = space >= 0 ? sub.slice(space + 1) : "";
        return `<div class="oc-sub"><span class="oc-sub-h">${escapeHtml(head)}</span><span class="oc-sub-t">${escapeHtml(rest ? ` ${rest}` : "")}</span></div>`;
      })
      .join("");
    return `<div class="oc-item-wrap">
      <span class="oc-item-key">${key}.</span>
      <div class="oc-item-body">
        <div class="oc-intro">The orientation content for all contractors will include the following as applicable and appropriate to the care and service provided:</div>
        <div class="oc-subs">${subs}</div>
      </div>
    </div>`;
  }
  return `<div class="oc-item-wrap">
    <span class="oc-item-key">${key}.</span>
    <div class="oc-item-body"><span class="oc-plain">${escapeHtml(itemDef.text)}</span></div>
  </div>`;
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildOrientationChecklistPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const contractorName = escapeHtml(
    String(
      record.contractorName ||
        record.applicantName ||
        record.applicantSignedBy ||
        "",
    ).trim(),
  );
  const formDate = escapeHtml(formatDate(record.date));
  const position = escapeHtml(String(record.positionAppliedFor || ""));

  const merged = mergeChecklistRows(/** @type {unknown[]} */ (record.items));

  const rowsHtml = merged
    .map((row, index) => {
      const itemDef = CHECKLIST_ITEMS[index];
      const desc = itemDef ? buildChecklistDescriptionCell(itemDef) : "";
      const initials = escapeHtml(row.contractorInitials || "");
      const d = escapeHtml(formatDate(row.dateOfOrientation));
      return `<tr>
        <td class="oc-td oc-desc">${desc}</td>
        <td class="oc-td oc-c">${initials || "—"}</td>
        <td class="oc-td oc-c">${d || "—"}</td>
      </tr>`;
    })
    .join("");

  const applicantSig = record.applicantSignature;
  const applicantSignedAt = escapeHtml(
    formatDate(record.applicantSignedAt),
  );
  const applicantSignedBy = escapeHtml(
    String(record.applicantSignedBy || "").trim(),
  );
  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || "").trim(),
  );
  const reviewedBySig = record.reviewedBySignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(record.reviewedBySignedAt),
  );

  const employeeDisplayName =
    contractorName || applicantSignedBy || "—";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Orientation Checklist — ${contractorName || "Contractor"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 10px;
      line-height: 1.4;
      color: #111827;
      margin: 0;
      padding: 10px;
    }
    .sheet { max-width: 900px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 28px 32px 36px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 14px;
    }
    .logo { height: 52px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 12px; font-weight: 500; color: #2563eb; margin: 0; }
    .oc-meta {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 12px;
      margin-bottom: 16px;
    }
    .oc-meta-lbl { font-size: 9px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 4px; }
    .oc-meta-val {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 11px;
      min-height: 34px;
    }
    .oc-table { width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
    .oc-th { text-align: left; padding: 10px 12px; background: #f8fafc; border-bottom: 1px solid #e5e7eb; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.04em; }
    .oc-th.oc-c, .oc-c { text-align: center; }
    .oc-th.oc-narrow { width: 100px; white-space: nowrap; }
    .oc-th.oc-date { width: 120px; white-space: nowrap; }
    .oc-td { padding: 10px 12px; border-top: 1px solid #f1f5f9; vertical-align: top; }
    .oc-desc { color: #1e293b; }
    .oc-item-wrap { display: flex; gap: 10px; align-items: flex-start; }
    .oc-item-key { font-weight: 700; min-width: 22px; flex-shrink: 0; }
    .oc-item-body { flex: 1; min-width: 0; }
    .oc-plain { font-weight: 500; }
    .oc-intro { font-size: 10px; font-weight: 500; margin-bottom: 8px; line-height: 1.45; }
    .oc-subs { border-left: 2px solid #e2e8f0; padding-left: 10px; margin-left: 2px; }
    .oc-sub { font-size: 9.5px; color: #475569; margin-bottom: 4px; line-height: 1.35; }
    .oc-sub-h { font-weight: 700; color: #0f172a; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
      margin-top: 18px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 12px; font-weight: 700; margin: 0 0 10px 0; color: #111827; }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 26px;
      font-size: 11px;
      padding: 4px 0;
    }
    .sig-img { max-height: 44px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 18px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 9px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Orientation checklist</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <div class="oc-meta">
      <div>
        <div class="oc-meta-lbl">Contractor</div>
        <div class="oc-meta-val">${contractorName || "—"}</div>
      </div>
      <div>
        <div class="oc-meta-lbl">Date</div>
        <div class="oc-meta-val">${formDate || "—"}</div>
      </div>
      <div>
        <div class="oc-meta-lbl">Position applied for</div>
        <div class="oc-meta-val">${position || "—"}</div>
      </div>
    </div>

    <table class="oc-table">
      <thead>
        <tr>
          <th class="oc-th">Checklist</th>
          <th class="oc-th oc-c oc-narrow">Contractor initials</th>
          <th class="oc-th oc-c oc-date">Date of orientation</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>

    <div class="two-col">
      <div class="sig-card">
        <h3>Contractor / employee</h3>
        <div class="sig-label">Name</div>
        <div class="oc-meta-val" style="margin-bottom:8px;">${employeeDisplayName}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="oc-meta-val" style="margin-bottom:8px;">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
